/**
 * HeaderSlotContext — BLU-3xx Phase 47.01.2-B-fix3.4
 *
 * Lets a page register a ReactNode that renders inside the global TopHeader,
 * left of the centred page title and right of the SidebarTrigger / back
 * button. Mirrors the `PageTitleContext` pattern so both slots share the
 * same provider/effect shape.
 *
 * Use `useHeaderLeftSlot(node)` from a page. Pass `null` (or omit) to clear.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

/*
 * Two contexts, so that SETTING the slot never re-renders the page that set it
 * (same split as PageTitleContext, BLU-1374).
 *
 * - The setter context holds `setState` from useState, which React keeps the
 *   same for the provider's whole life. Pages only read this one.
 * - The value context holds the node itself, so only readers (TopHeader)
 *   re-render when a page sets a new one.
 *
 * One context used to carry `{ leftSlot, setLeftSlot }`, a new object every
 * render, and the hook's effect depended on it. Setting the slot re-rendered
 * the page; with a node that was not memoised, the effect then set the slot
 * again, forever.
 */
const HeaderSlotSetterContext = createContext<((node: ReactNode | null) => void) | null>(null);
const HeaderSlotValueContext = createContext<ReactNode | null>(null);

export function HeaderSlotProvider({ children }: { children: ReactNode }) {
  const [leftSlot, setLeftSlot] = useState<ReactNode | null>(null);
  return (
    <HeaderSlotSetterContext.Provider value={setLeftSlot}>
      <HeaderSlotValueContext.Provider value={leftSlot}>{children}</HeaderSlotValueContext.Provider>
    </HeaderSlotSetterContext.Provider>
  );
}

/** Read-only consumer used by TopHeader. `null` outside a provider. */
export function useHeaderSlotValue() {
  return useContext(HeaderSlotValueContext) ?? null;
}

/**
 * Register a node into the header's left-slot; cleared on unmount. A no-op
 * outside a provider.
 *
 * The node is compared by identity. An inline JSX node is new on every render
 * of the page, so it re-sets the slot once each time the page renders (never
 * in a loop). Memoise it (typically `useMemo`) if the page re-renders often.
 */
export function useHeaderLeftSlot(node: ReactNode | null) {
  const setLeftSlot = useContext(HeaderSlotSetterContext);
  useEffect(() => {
    if (!setLeftSlot) return;
    setLeftSlot(node);
    return () => setLeftSlot(null);
  }, [setLeftSlot, node]);
}
